import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Building2, Calendar, MapPin } from 'lucide-react';
import profileData from '../data/profileData.json';

const Experience = () => {
  const { experience } = profileData;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-4">Professional Experience</h1>
          <div className="h-1 w-20 bg-[#2563eb]"></div>
          <p className="mt-6 text-lg text-gray-600">
            10+ years of professional experience across leading organizations and diverse domains.
          </p>
        </div>

        {/* Experience Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-300"></div>

          {/* Experience Items */}
          <div className="space-y-12">
            {experience.map((exp, index) => (
              <div key={exp.id} className="relative">
                {/* Timeline Dot */}
                <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 top-0">
                  <div className="w-4 h-4 bg-[#2563eb] rounded-full border-4 border-white"></div>
                </div>

                {/* Content Card */}
                <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:ml-auto md:pl-12'}`}>
                  <Card className="hover:shadow-lg transition-shadow duration-200">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-2xl text-[#1e3a5f] mb-2">
                            {exp.company}
                          </CardTitle>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-gray-600">
                              <MapPin size={16} />
                              <span>{exp.location}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <Calendar size={16} />
                              <span>{exp.duration}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <Badge className="bg-blue-100 text-blue-800" variant="secondary">
                            {exp.domain}
                          </Badge>
                        </div>

                        {/* Positions */}
                        <div className="space-y-4">
                          {exp.positions.map((position, posIndex) => (
                            <div key={posIndex} className="border-l-2 border-[#2563eb] pl-4">
                              <h4 className="font-semibold text-[#1e3a5f] mb-1">
                                {position.title}
                              </h4>
                              <p className="text-sm text-gray-500 mb-2">{position.duration}</p>
                              <p className="text-gray-600 text-sm leading-relaxed">
                                {position.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Domain Expertise Summary */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-8">Domain Expertise</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Building2 size={24} className="text-[#2563eb]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f]">Financial Services</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Retirement planning, investment management, and wealth management solutions for major financial institutions.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Building2 size={24} className="text-[#2563eb]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f]">Professional Services</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Audit, compliance, and learning platforms for global consulting firms like KPMG.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Building2 size={24} className="text-[#2563eb]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f]">Technology & AI</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  AI-powered testing platforms, software quality assurance, and machine learning solutions.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Experience;